type TicketMutationKind = 'create' | 'assign' | 'status' | 'patch' | 'comment';

export type TicketMutationRecord = {
  id: string;
  kind: TicketMutationKind;
  payload: Record<string, any>;
  createdAt: number;
  nextAttemptAt: number;
  attempts: number;
  lastError?: string | null;
};

const DB_NAME = 'cybard_ticket_sync';
const DB_VERSION = 1;
const STORE_NAME = 'mutation_outbox';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('nextAttemptAt', 'nextAttemptAt', { unique: false });
        store.createIndex('createdAt', 'createdAt', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function withStore<T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => Promise<T> | T,
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, mode);
        const store = tx.objectStore(STORE_NAME);
        Promise.resolve(work(store))
          .then((value) => {
            tx.oncomplete = () => resolve(value);
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error);
          })
          .catch((error) => reject(error));
      }),
  );
}

export async function enqueueTicketMutation(
  mutation: Omit<TicketMutationRecord, 'createdAt' | 'nextAttemptAt' | 'attempts' | 'lastError'>,
) {
  const record: TicketMutationRecord = {
    ...mutation,
    createdAt: Date.now(),
    nextAttemptAt: Date.now(),
    attempts: 0,
    lastError: null,
  };
  await withStore('readwrite', async (store) => {
    if (
      (record.kind === 'status' || record.kind === 'assign') &&
      record.payload?.ticketId
    ) {
      const request = store.getAll();
      const rows = await new Promise<TicketMutationRecord[]>((resolve, reject) => {
        request.onsuccess = () =>
          resolve((request.result || []) as TicketMutationRecord[]);
        request.onerror = () => reject(request.error);
      });
      const ticketId = String(record.payload.ticketId);
      for (const row of rows) {
        if (row.kind !== record.kind) continue;
        if (String(row.payload?.ticketId || '') !== ticketId) continue;
        store.delete(row.id);
      }
    }
    store.put(record);
  });
  return record;
}

export async function listReadyTicketMutations(limit = 20) {
  const now = Date.now();
  const rows = await withStore('readonly', async (store) => {
    const request = store.getAll();
    return await new Promise<TicketMutationRecord[]>((resolve, reject) => {
      request.onsuccess = () => resolve((request.result || []) as TicketMutationRecord[]);
      request.onerror = () => reject(request.error);
    });
  });
  return rows
    .filter((row) => row.nextAttemptAt <= now)
    .sort((a, b) => a.createdAt - b.createdAt)
    .slice(0, limit);
}

export async function markTicketMutationDone(id: string) {
  await withStore('readwrite', async (store) => {
    store.delete(id);
  });
}

export async function markTicketMutationRetry(
  id: string,
  lastError: string,
) {
  await withStore('readwrite', async (store) => {
    const getReq = store.get(id);
    const existing = await new Promise<TicketMutationRecord | null>((resolve, reject) => {
      getReq.onsuccess = () =>
        resolve((getReq.result as TicketMutationRecord | undefined) ?? null);
      getReq.onerror = () => reject(getReq.error);
    });
    if (!existing) return;
    const attempts = (existing.attempts || 0) + 1;
    const exp = Math.min(8, Math.max(0, attempts - 1));
    const delayMs = Math.min(300000, 1000 * 2 ** exp);
    store.put({
      ...existing,
      attempts,
      nextAttemptAt: Date.now() + delayMs,
      lastError: String(lastError || 'mutation_failed').slice(0, 400),
    } as TicketMutationRecord);
  });
}

export async function clearTicketMutationOutbox() {
  await withStore('readwrite', async (store) => {
    store.clear();
  });
}
